import { useEffect, useState, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { UserRole } from '../../../stores/authStore';
import { testMgr } from '../utils/oidcConfig';
import { checkAndCleanStorage, cleanAllAuthStorage, debugAuthStorage } from '../utils/storageUtils';
import vinkLogo from '@/assets/vinklogo.png';

// 🆕 定义 vinka 用户禁止访问的路由
const Vinka_RESTRICTED_ROUTES = ['/app/workflow', '/app/runlog'];


// 简单的加载页面组件
const LoadingPage = ({ message = '加载中...' }: { message?: string }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a192f] via-[#112240] to-[#0a192f] text-white flex items-center justify-center">
      {/* Decorative background elements */}
      <div className="fixed inset-0 -z-10 opacity-20 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full filter blur-[128px]"></div>
        <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-indigo-500 rounded-full filter blur-[96px]"></div>
        <div className="absolute top-2/3 left-1/3 w-80 h-80 bg-cyan-400 rounded-full filter blur-[120px]"></div>
      </div>

      <div className="flex flex-col items-center gap-6 z-10">
        <div className="w-16 h-16 relative">
          <div className="absolute inset-0 rounded-full border-2 border-t-blue-400 border-blue-400/20 animate-spin"></div>
          <div className="absolute inset-3 rounded-full border-2 border-r-indigo-400 border-indigo-400/20 animate-spin animate-reverse"></div>
        </div>
        <span className="text-lg text-blue-50/80">{message}</span>

        {/* Vink Logo */}
        <div className="absolute bottom-10 inset-x-0 flex justify-center">
          <div className="flex items-center">
            <img
              src={vinkLogo}
              alt="Vink Logo"
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 路由守卫组件，用于保护需要认证的路由
 * 检查用户是否已登录，如果未登录则重定向到登录页面
 */
const RouteGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ⚡ P0优化：使用同步检查实现“乐观渲染”
  const { setUser, setToken, isMossUser } = useAuthStore();
  const quickCheckToken = localStorage.getItem('access_token');
  const alreadyAuthenticated = !!quickCheckToken;

  const [isChecking, setIsChecking] = useState(!alreadyAuthenticated);
  const authCheckedRef = useRef(false); // 🆕 追踪是否已完成认证检查

  const whiteList = ['/auth-redirect', '/heroesRank', 'dataVisualization', '/getFile', '/callBack']

  // 是否跳过SSO身份验证（开发模式下可选）
  const skipSso = import.meta.env.VITE_SKIP_SSO === 'true';
  // 是否显示SSO调试信息
  const enableSsoDebug = import.meta.env.VITE_ENABLE_SSO_DEBUG === 'true';

  // 🆕 Vinka 用户路由限制：检测是否尝试访问被禁止的路由
  useEffect(() => {
    if (isMossUser()) {
      const isRestrictedRoute = Vinka_RESTRICTED_ROUTES.some(route =>
        location.pathname.startsWith(route)
      );
      if (isRestrictedRoute) {
        console.log('[RouteGuard] 🚫 Vinka 用户访问受限路由，重定向到 /app/moss-chat');
        navigate('/app/moss-chat', { replace: true });
      }
    }
  }, [location.pathname, isMossUser, navigate]);

  useEffect(() => {
    const checkAuthentication = async () => {
      // ⚡ P0修复：快速退出逻辑 - 避免重复认证检查
      // 如果用户已认证且有 token，立即返回，不执行后续耗时检查
      const currentState = useAuthStore.getState();
      const tokenInStorage = localStorage.getItem('access_token');

      // 🆕 如果已经完成过认证检查，且用户仍有有效 token，直接跳过
      if (authCheckedRef.current && currentState.isAuthenticated && tokenInStorage) {

        setIsChecking(false);
        return;
      }

      if (currentState.isAuthenticated && currentState.user && tokenInStorage) {
        setIsChecking(false);
        authCheckedRef.current = true; // 标记已完成认证

        return;
      }

      // 🔥 关键修复：首先检查并清理过期的存储数据

      const wasClean = checkAndCleanStorage();
      if (wasClean) {

      }

      // 调试：打印当前认证存储状态
      if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'master') {
        debugAuthStorage();
      }






      // 🆕 新策略：不再自动触发 SSO，改为默认让用户从 /welcome 页面手动登录
      // 这样老用户（有 access_token）不受影响，新用户默认走 Moss 登录流
      if (skipSso) {
        // 获取当前认证状态
        const currentState = useAuthStore.getState();

        // 如果已认证，直接通过
        if (currentState.isAuthenticated && currentState.user) {
          setIsChecking(false);
          return;
        }

        // 未认证，重定向到 /welcome 让用户手动选择登录方式
        navigate('/welcome', { replace: true });
        return;
      }

      // 第一步，检测是否存在access_token (老用户 SSO)
      const accessToken = localStorage.getItem('access_token');



      if (accessToken) {

      }

      if (accessToken) {
        // 第二步，存在access_token，设置用户状态


        // 获取最新状态（避免使用依赖项中的状态）
        const currentState = useAuthStore.getState();

        // 如果用户未认证，创建一个基本用户对象
        if (!currentState.isAuthenticated || !currentState.user) {
          // 从token中提取基本信息或使用默认值
          const mockUser = {
            id: 'token-user',
            username: 'Token User',
            email: 'user@example.com',
            role: 'user' as UserRole,
            permissions: ['workflow:view', 'runlog:view']
          };

          setUser(mockUser);
          setToken(accessToken);

        }

        // 完成认证检查
        setIsChecking(false);

        return;
      }

      // 没有token，并且不是白名单
      // ✅ 关键修复：先尝试从 UserManager 恢复，而不是直接清理
      if (!whiteList.includes(location.pathname)) {
        // 尝试从 UserManager 恢复 token
        try {
          const user = await testMgr.getUser();
          if (user?.access_token && !user.expired) {
            // UserManager 有有效会话，同步到 localStorage
            localStorage.setItem('access_token', user.access_token);

            // 设置用户状态
            const mockUser = {
              id: 'token-user',
              username: 'Token User',
              email: 'user@example.com',
              role: 'user' as UserRole,
              permissions: ['workflow:view', 'runlog:view']
            };
            setUser(mockUser);
            setToken(user.access_token);

            setIsChecking(false);
            authCheckedRef.current = true;
            console.log('[RouteGuard] 从 UserManager 恢复 token 成功');
            return;
          }
        } catch (e) {
          console.warn('[RouteGuard] 从 UserManager 恢复失败:', e);
        }

        // ✅ 修复：不再在 signinRedirect 前调用 cleanAllAuthStorage()
        // 原因：这会删掉 oidc.* state，可能导致回调失败







        // 移动端调试：显示关键信息
        const queryDebugEnabled = new URLSearchParams(window.location.search).get('debug') === 'true';
        if (enableSsoDebug || queryDebugEnabled) {
          alert(`🔍 SSO调试信息\n环境: ${import.meta.env.MODE}\n路径: ${location.pathname}\nSSO: ${import.meta.env.VITE_OIDC_SSO_URL}\nClient: ${import.meta.env.VITE_OIDC_CLIENT_ID}`);
        }

        try {
          // 🔥 关键修复：先检查OIDC配置是否可访问

          const configUrl = `${import.meta.env.VITE_API_BASE_URL}/.well-known/openid-configuration`;


          // 测试配置端点是否可访问（5秒超时）
          try {
            const configResponse = await Promise.race([
              fetch(configUrl, { method: 'GET', mode: 'cors' }),
              new Promise<Response>((_, reject) => setTimeout(() => reject(new Error('配置端点超时')), 5000))
            ]);

            if (!configResponse.ok) {
              throw new Error(`配置端点返回错误: ${configResponse.status}`);
            }
          } catch (configError) {

            const errorMsg = configError instanceof Error ? configError.message : '配置端点不可访问';

            // 提供清除缓存选项
            const userChoice = confirm(`⚠️ SSO配置检查失败\n\n${errorMsg}\n\n配置URL: ${configUrl}\n\n这可能是网络问题或SSO服务异常。\n\n点击"确定"清除缓存并重试\n点击"取消"停留在当前页面`);

            if (userChoice) {

              cleanAllAuthStorage();
              setTimeout(() => {
                window.location.reload();
              }, 300);
            } else {
              setIsChecking(false);
            }
            return;
          }



          // 🔥 诊断信息：检查协议和加密支持


          // 🔥 关键修复：添加30秒超时保护
          const redirectPromise = testMgr.signinRedirect({ state: window.location.href });
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('SSO认证超时：30秒内未响应')), 30000);
          });

          await Promise.race([redirectPromise, timeoutPromise]);

        } catch (error) {






          // 🔥 增强错误信息：提取完整错误消息并分析
          let errorMsg = '未知错误';
          let errorDetails = '';

          if (error instanceof Error) {
            errorMsg = error.message;

            // 检查是否是特定类型的错误
            if (errorMsg.includes('Failed to fetch')) {
              errorDetails = '\n\n可能原因:\n1. 网络连接问题\n2. CORS跨域限制\n3. SSO服务器不可达';

              // 检查HTTPS
              if (window.location.protocol !== 'https:') {
                errorDetails += '\n⚠️ 当前使用HTTP协议，OIDC需要HTTPS';
              }

              // 检查Crypto API
              if (typeof window.crypto?.subtle === 'undefined') {
                errorDetails += '\n⚠️ Crypto.subtle不可用';
              }
            } else if (errorMsg.includes('timeout') || errorMsg.includes('超时')) {
              errorDetails = '\n\n原因: SSO服务器响应超时';
            }
          }



          // 移动端错误提示 + 清除缓存选项
          const userChoice = confirm(`❌ SSO认证失败\n\n${errorMsg}${errorDetails}\n\n时间: ${new Date().toLocaleTimeString()}\n\n点击"确定"清除缓存并重试\n点击"取消"停留在当前页面`);

          if (userChoice) {

            cleanAllAuthStorage();
            setTimeout(() => {
              window.location.reload();
            }, 300);
          } else {
            // 超时后进入降级模式：显示错误页面而不是一直转圈
            setIsChecking(false);
          }
        }
      } else {

        setIsChecking(false);
      }
    };

    checkAuthentication();
    // 移除 isAuthenticated 和 user 依赖，防止循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, skipSso]);

  // 认证检查中显示加载状态
  if (isChecking) {
    return <LoadingPage message="正在验证用户身份..." />;
  }

  // 如果跳过SSO，直接渲染子路由
  if (skipSso) {
    return <Outlet />;
  }

  const accessToken = localStorage.getItem('access_token');

  // 已通过认证，渲染子路由
  if (accessToken || whiteList.includes(location.pathname)) {
    return <Outlet />;
  }

  // 未通过认证且不在白名单，显示加载页面（等待重定向）
  return <LoadingPage message="正在跳转到登录页面..." />;
};

export default RouteGuard;
